import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import classnames from "classnames";
import Button from "../../../../../../../../components/base/button/button";
import Api from "../../../../../../../../../core/services/api/api";
import {deepEqual, numComparator} from "../../../../../../../../../core/services/utils/utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../../../hooks/use-price-level-dictionary";

const BulkPriceChangePriceLevelSelectionSection = forwardRef(({
                                                                  selectedPriceLevelIds,
                                                                  setPriceLevelSelectionState,
                                                                  requestId,
                                                              }, ref) => {
    const [loading, setLoading] = useState(false);
    const [priceLevels, setPriceLevels] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * Attaches a [submit] callback to the ref object of this component.
     */
    useImperativeHandle(ref, () => ({
        async submit() {
            return saveBulkPriceChangePriceLevels();
        }
    }), [requestId, selectedIds, priceLevels])


    /**
     * With each change in the [selectedIds] value of the state:
     * - sets a timer at the end of which the selectedIds will be saved for the request in the server.
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            saveBulkPriceChangePriceLevels().then();
        }, 1000);
        return () => {
            clearTimeout(timer);
        }
    }, [selectedIds])

    /**
     * With each change in the [selectedPriceLevelIds] prop value:
     * - syncs the state value of the [selectedIds] with the prop counterpart..
     */
    useEffect(() => {
        setSelectedIds(selectedPriceLevelIds);
    }, [selectedPriceLevelIds])

    /**
     * With initial render of the component calls the function for getting price levels
     */
    useEffect(() => {
        getPriceLevels().then();
    }, [])

    /**
     * Calls the api for getting the list of price levels:
     * - if the api call was successful, populates the [priceLevels] in the inner state
     * @return {Promise<void>}
     */
    const getPriceLevels = async () => {
        setLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getPartnerPriceLevels(),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setPriceLevels(response?.data?.map(e => ({
                ...e,
                title: priceLevelDict?.find(p => p.priceLevelId === e.id)?.title ?? e.title
            })) ?? [])
        }
    }

    /**
     * Saves the selected price levels of the current bulk price change request.
     * @return {Promise<boolean>}
     */
    const saveBulkPriceChangePriceLevels = async () => {
        const forApi = {
            requestId: requestId,
            priceLevelIds: selectedIds ?? []
        }
        if (deepEqual(Array.from(forApi.priceLevelIds).sort(numComparator), Array.from(selectedPriceLevelIds).sort(numComparator)))
            return true;
        const response = await Api.saveBulkPriceChangeSelectedPriceLevels(forApi);
        if (response?.resultFlag) {
            setPriceLevelSelectionState(priceLevels.filter(e => selectedIds.includes(e.id)));
        }
        return response?.resultFlag;
    }

    /**
     * Handles click over the price level card:
     * - if the price levels exists in the list of selected price levels removes it from selected items
     * - otherwise, removes the selected price level from the selected items
     * @param priceLevel
     */
    const onPriceLevelCardClicked = (priceLevel) => {
        if (selectedIds.includes(priceLevel.id)) {
            setSelectedIds(prevState => prevState.filter(e => e !== priceLevel.id));
            return;
        }
        setSelectedIds(prevState => [...prevState, priceLevel.id]);
    }

    /**
     * Selects all the price levels.
     */
    const onSelectAll = () => {
        setSelectedIds(priceLevels.map(e => e.id));
    }

    return (
        <>
            <Paper className={'paper content price-level-selection'} style={{borderRadius: '0 0 5px 5px'}}>
                <Row className='no-gutters w-100 d-flex justify-content-between align-items-center mb-4 px-4'>
                    <p className={'note'}>
                        Please select at least one price level to continue
                    </p>
                    {
                        priceLevels.length > 0
                            ? <Button
                                style={{height: '45px'}}
                                className='ml-3'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={onSelectAll}
                            >
                                Select All
                            </Button>
                            : <div/>
                    }
                </Row>
                <Row className={'w-90 mx-5'}>
                    {
                        loading
                            ? Array(10).fill(null).map((_, index) =>
                                <div key={index} className={'loading price-level-card-loading'}>
                                    <div/>
                                </div>
                            )
                            : priceLevels.map(e => (
                                <Col key={e.id} xs={12} md={6} className={'mb-2 no-gutters'}>
                                    <div
                                        className={classnames('price-level-card', {'selected': selectedIds.includes(e.id)})}
                                        onClick={() => onPriceLevelCardClicked(e)}
                                    >
                                        <p>
                                            {e.title}
                                        </p>
                                    </div>
                                </Col>
                            ))
                    }
                </Row>
            </Paper>
        </>
    );
})

export default BulkPriceChangePriceLevelSelectionSection;
